import { createClient } from '@supabase/supabase-js';
import { SubscriptionProduct, FeaturedEventProduct } from './types';

const createCheckoutSession = async (
  priceId: string,
  mode: 'payment' | 'subscription',
  successUrl: string,
  cancelUrl: string,
  customerId?: string,
  accessToken?: string,
  metadata: Record<string, string> = {}
): Promise<{ url?: string; sessionId?: string }> => {
  try {
    if (!accessToken) {
      throw new Error('Access token is required');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        price_id: priceId,
        mode,
        success_url: successUrl,
        cancel_url: cancelUrl,
        customer_id: customerId,
        metadata
      })
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Server error creating checkout:', result);
      throw new Error(result.error || 'Failed to create checkout session');
    }

    return result;
  } catch (err) {
    console.error('Error creating checkout session:', err);
    throw err;
  }
};

// Subscription products configuration
export const subscriptionProducts: Record<string, SubscriptionProduct> = {
  subscribedMonthly: {
    id: 'subscribed-monthly',
    priceId: import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1SFLvFAplG2SvDTIqKCELcrn',
    name: 'Subscribed Monthly',
    description: '3 Vybes created per month',
    price: 3.99,
    mode: 'subscription',
    interval: 'month',
    durationMonths: 1,
    trialDays: 0
  }
};

// Token top-up product configuration
export interface TokenTopUpProduct {
  id: string;
  priceId: string;
  name: string;
  description: string;
  price: number;
  mode: 'payment';
  tokens: {
    create: number;
    apply: number;
    accepted: number;
  };
}

export const tokenTopUpProduct: TokenTopUpProduct = {
  id: 'token-topup',
  priceId: import.meta.env.VITE_STRIPE_PRICE_TOKEN_TOPUP || 'price_1ST1MGAplG2SvDTIHZMSjH2u',
  name: 'Host Token Top-Up',
  description: '+1 Vybe creation',
  price: 2.49,
  mode: 'payment',
  tokens: {
    create: 1,
    apply: 0,
    accepted: 0
  }
};

// Featured event products configuration
export const featuredEventProducts: Record<string, FeaturedEventProduct> = {
  featured3Days: {
    id: 'featured-3-days',
    priceId: import.meta.env.VITE_STRIPE_PRICE_FEATURED_3_DAYS || 'price_1RGf28AplG2SvDTIX2P24kjX',
    name: 'Featured Event - 3 Days',
    description: 'Make your event featured for 3 days',
    price: 5.99,
    mode: 'payment',
    duration: 3
  },
  featured7Days: {
    id: 'featured-7-days',
    priceId: import.meta.env.VITE_STRIPE_PRICE_FEATURED_7_DAYS || 'price_1RGf2YAplG2SvDTIT65MH69w',
    name: 'Featured Event - 7 Days',
    description: 'Make your event featured for 7 days',
    price: 12.99,
    mode: 'payment',
    duration: 7
  },
  featured14Days: {
    id: 'featured-14-days',
    priceId: import.meta.env.VITE_STRIPE_PRICE_FEATURED_14_DAYS || 'price_1RGf2zAplG2SvDTIQCRvDDk5',
    name: 'Featured Event - 14 Days',
    description: 'Make your event featured for 14 days',
    price: 15.99,
    mode: 'payment',
    duration: 14
  }
};

export { createCheckoutSession };